'use client';

import Image from 'next/image';
import useRoute from '@/ueshook/useRoute';
import {
  InsIcon,
  TwitterIcon,
  TtIcon,
  LemonIcon,
  Discord,
} from '@/components/icon/star';
import Link from 'next/link';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/utils/cn';

// 导入纯对象，避免使用routing对象中的函数
const LOCALES = ['en', 'zh', 'pt', 'es', 'ru'];
const DEFAULT_LOCALE = 'en';

const logo = {
  src: 'https://imagedelivery.net/7rExjEZEypWvglDwZ5-zxA/ffee010c-d40e-437a-e26f-ecd79eda1b00/public',
  alt: 'Kaze AI Logo',
};

// 语言配置
const languages = [
  { value: 'en', label: 'English (US)' },
  { value: 'zh', label: '简体中文' },
  { value: 'pt', label: 'Português (Brasil)' },
  { value: 'es', label: 'Español (Latinoamérica)' },
  { value: 'ru', label: 'Русский' },
];

export default function Footer() {
  const { push } = useRoute();
  const [value, setValue] = useState('');

  useEffect(() => {
    const detectCurrentLanguage = () => {
      const path = window.location.pathname;
      // 分割路径并过滤空字符串
      const segments = path.split('/').filter(Boolean);
      // 获取第一段作为可能的语言代码
      const firstSegment = segments[0] || '';

      if (LOCALES.includes(firstSegment)) {
        setValue(firstSegment);
      } else {
        setValue(DEFAULT_LOCALE);
      }
    };

    detectCurrentLanguage();
  }, []);

  const getNewPathForLanguage = (newLanguage: string) => {
    const currentPath = window.location.pathname;

    const segments = currentPath.split('/').filter(Boolean);

    const hasLocalePrefix =
      segments.length > 0 && LOCALES.includes(segments[0]);

    if (hasLocalePrefix) {
      const pathWithoutLocale =
        segments.length > 1 ? '/' + segments.slice(1).join('/') : '';

      return `/${newLanguage}${pathWithoutLocale}`;
    } else {
      return `/${newLanguage}${currentPath === '/' ? '' : currentPath}`;
    }
  };

  // 语言选择器组件 - 使用自己的状态
  const LanguageSelector = () => {
    const [open, setOpen] = useState(false);

    // 处理语言选择
    const handleLanguageSelect = (lang: string) => {
      // 计算新路径
      const newPath = getNewPathForLanguage(lang);

      // 更新状态并关闭下拉菜单
      setValue(lang);
      setOpen(false);

      // 使用新路径导航
      window.location.href = newPath;
    };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            role='combobox'
            aria-expanded={open}
            className='w-[220px] justify-between'
          >
            <div className='flex items-center'>
              <Globe className='mr-2 h-4 w-4' />
              {value
                ? languages.find((language) => language.value === value)?.label
                : 'Select language...'}
            </div>
            <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-[220px] p-0'>
          <Command>
            <CommandInput placeholder='Search language...' />
            <CommandList>
              <CommandEmpty>No language found.</CommandEmpty>
              <CommandGroup>
                {languages.map((language) => (
                  <CommandItem
                    key={language.value}
                    value={language.value}
                    onSelect={() => handleLanguageSelect(language.value)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === language.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {language.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  };

  const IconList = (
    <>
      <a href='https://www.instagram.com/kazeai_official/'>
        <InsIcon />
      </a>
      <a href='https://x.com/KazeAI_official'>
        <TwitterIcon />
      </a>
      <a href='https://www.tiktok.com/@kazeai_official'>
        <TtIcon />
      </a>
      <a href='https://www.lemon8-app.com/@kazeai_official?region=us'>
        <LemonIcon />
      </a>
      <a href='https://discord.gg/dz3ndW4R8j' className='-mt-1'>
        <Discord />
      </a>
    </>
  );

  return (
    <footer className='footer-container bg-transparent px-5 py-12 lg:px-28'>
      {/* 移动端视图 */}
      <div className='flex w-full flex-col items-start space-y-8 lg:hidden'>
        <div className='flex w-full flex-col'>
          <Image
            src={logo.src}
            alt={logo.alt}
            className='z-50 mb-8 h-[45px] w-[150px] cursor-pointer'
            width={150}
            height={45}
            onClick={() => push('/')}
          />
          <div className='mb-5 flex gap-x-4'>{IconList}</div>
          <span className='mb-4 text-gray-700'>@2025 Kaze</span>
          <LanguageSelector />
        </div>

        <div className='grid w-full grid-cols-2 gap-y-8'>
          <div className='w-fit space-y-3 text-sm'>
            <h3 className='font-semibold'>AI Removal Suite</h3>
            <div className='flex flex-col space-y-2'>
              <Link
                href='/watermark-removal'
                className='text-gray-700 hover:text-black'
              >
                Watermark Removal
              </Link>
              <Link
                href='/remove-text-from-image'
                className='text-gray-700 hover:text-black'
              >
                Text Removal
              </Link>
              <Link
                href='/logo-remover'
                className='text-gray-700 hover:text-black'
              >
                Logo Removal
              </Link>
              <Link
                href='/remove-signature'
                className='text-gray-700 hover:text-black'
              >
                Signature Removal
              </Link>
              <Link
                href='/photo-stamp-remover'
                className='text-gray-700 hover:text-black'
              >
                Stamp Removal
              </Link>
              <Link
                href='/sticker-remover'
                className='text-gray-700 hover:text-black'
              >
                Sticker Removal
              </Link>
            </div>
          </div>

          <div className='w-fit'>
            <div className='mb-8 w-fit space-y-3 text-sm'>
              <h3 className='font-semibold'>Professional Portrait</h3>
              <div className='flex flex-col space-y-2'>
                <Link
                  href='/ai-headshot-generator'
                  className='text-gray-700 hover:text-black'
                >
                  AI Headshot
                </Link>
              </div>
            </div>

            <div className='w-fit space-y-3 text-sm'>
              <h3 className='font-semibold'>Image Restoration</h3>
              <div className='flex flex-col space-y-2'>
                <Link
                  href='/photo-restoration'
                  className='text-gray-700 hover:text-black'
                >
                  Photo Restoration
                </Link>
                <Link
                  href='/unblur-image'
                  className='text-gray-700 hover:text-black'
                >
                  Image Unblur
                </Link>
                <Link
                  href='/image-enhancer'
                  className='text-gray-700 hover:text-black'
                >
                  Image Enhancer
                </Link>
                <Link
                  href='/image-upscaler'
                  className='text-gray-700 hover:text-black'
                >
                  Image Upscaler
                </Link>
              </div>
            </div>
          </div>

          <div className='w-fit space-y-3 text-sm'>
            <h3 className='font-semibold'>Background Removal</h3>
            <div className='flex flex-col space-y-2'>
              <Link
                href='/background-remover'
                className='text-gray-700 hover:text-black'
              >
                Background Remover
              </Link>
              <Link
                href='/png-maker'
                className='text-gray-700 hover:text-black'
              >
                Png Maker
              </Link>
              <Link
                href='/transparent-background-maker'
                className='text-gray-700 hover:text-black'
              >
                Transparent Background
              </Link>
              <Link
                href='/white-background-maker'
                className='text-gray-700 hover:text-black'
              >
                White Background Maker
              </Link>
              <Link
                href='/black-background-maker'
                className='text-gray-700 hover:text-black'
              >
                Black Background Maker
              </Link>
            </div>
          </div>

          <div className='w-fit space-y-3 text-sm'>
            <h3 className='font-semibold'>Company</h3>
            <div className='flex flex-col space-y-2'>
              <Link href='/get-help' className='text-gray-700 hover:text-black'>
                Help Center
              </Link>
              <Link href='/terms' className='text-gray-700 hover:text-black'>
                Terms and Conditions
              </Link>
              <Link href='/privacy' className='text-gray-700 hover:text-black'>
                Privacy Policy
              </Link>
              <Link
                href='/fulfillment-policy'
                className='text-gray-700 hover:text-black'
              >
                Fulfillment Policy
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* 桌面端视图 - 左右布局 */}
      <div className='hidden w-full items-start justify-between gap-x-20 lg:flex'>
        {/* 左侧区域: Logo和版权 */}
        <div className='flex flex-col justify-between'>
          <div>
            <Image
              src={logo.src}
              alt={logo.alt}
              className='z-50 h-[45px] w-[150px] cursor-pointer'
              width={150}
              height={45}
              onClick={() => push('/')}
            />
            <div className='mt-5 flex gap-4'>{IconList}</div>
          </div>
          <div className='mt-10 flex flex-col gap-4'>
            <LanguageSelector />
            <span className='block text-gray-700'>@2025 Kaze</span>
          </div>
        </div>

        {/* 右侧区域: 功能链接 */}
        <div className='grid flex-1 grid-cols-2 md:grid-cols-4'>
          <div className='w-fit space-y-3 text-sm'>
            <h3 className='font-semibold'>AI Removal Suite</h3>
            <div className='flex flex-col space-y-2'>
              <Link
                href='/watermark-removal'
                className='text-gray-700 hover:text-black'
              >
                Watermark Removal
              </Link>
              <Link
                href='/remove-text-from-image'
                className='text-gray-700 hover:text-black'
              >
                Text Removal
              </Link>
              <Link
                href='/logo-remover'
                className='text-gray-700 hover:text-black'
              >
                Logo Removal
              </Link>
              <Link
                href='/remove-signature'
                className='text-gray-700 hover:text-black'
              >
                Signature Removal
              </Link>
              <Link
                href='/photo-stamp-remover'
                className='text-gray-700 hover:text-black'
              >
                Stamp Removal
              </Link>
              <Link
                href='/sticker-remover'
                className='text-gray-700 hover:text-black'
              >
                Sticker Removal
              </Link>
            </div>
          </div>

          <div className='flex flex-col space-y-8'>
            <div className='w-fit space-y-3 text-sm'>
              <h3 className='font-semibold'>Professional Portrait</h3>
              <div className='flex flex-col space-y-2'>
                <Link
                  href='/ai-headshot-generator'
                  className='text-gray-700 hover:text-black'
                >
                  AI Headshot
                </Link>
              </div>
            </div>

            <div className='w-fit space-y-3 text-sm'>
              <h3 className='font-semibold'>Image Restoration</h3>
              <div className='flex flex-col space-y-2'>
                <Link
                  href='/photo-restoration'
                  className='text-gray-700 hover:text-black'
                >
                  Photo Restoration
                </Link>
                <Link
                  href='/unblur-image'
                  className='text-gray-700 hover:text-black'
                >
                  Image Unblur
                </Link>
                <Link
                  href='/image-enhancer'
                  className='text-gray-700 hover:text-black'
                >
                  Image Enhancer
                </Link>
                <Link
                  href='/image-upscaler'
                  className='text-gray-700 hover:text-black'
                >
                  Image Upscaler
                </Link>
              </div>
            </div>
          </div>
          <div className='w-fit space-y-3 text-sm'>
            <h3 className='font-semibold'>Background Removal</h3>
            <div className='flex flex-col space-y-2'>
              <Link
                href='/background-remover'
                className='text-gray-700 hover:text-black'
              >
                Background Remover
              </Link>
              <Link
                href='/png-maker'
                className='text-gray-700 hover:text-black'
              >
                Png Maker
              </Link>
              <Link
                href='/transparent-background-maker'
                className='text-gray-700 hover:text-black'
              >
                Transparent Background
              </Link>
              <Link
                href='/white-background-maker'
                className='text-gray-700 hover:text-black'
              >
                White Background Maker
              </Link>
              <Link
                href='/black-background-maker'
                className='text-gray-700 hover:text-black'
              >
                Black Background Maker
              </Link>
            </div>
          </div>

          <div className='w-fit space-y-3 text-sm'>
            <h3 className='font-semibold'>Company</h3>
            <div className='flex flex-col space-y-2'>
              <Link href='/get-help' className='text-gray-700 hover:text-black'>
                Help Center
              </Link>
              <Link href='/terms' className='text-gray-700 hover:text-black'>
                Terms and Conditions
              </Link>
              <Link href='/privacy' className='text-gray-700 hover:text-black'>
                Privacy Policy
              </Link>
              <Link
                href='/fulfillment-policy'
                className='text-gray-700 hover:text-black'
              >
                Fulfillment Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
